import { logger } from '../utils/logger';
import { ErrorHandler } from '../utils/errorHandler';
import { RetryHelper } from '../utils/retryHelper';
import { stockDataCache } from '../utils/cacheService';
import { corsProxyService } from './corsProxyService';

export interface RawTpexQuote {
  Date: string;
  SecuritiesCompanyCode: string;
  CompanyName: string;
  Close: string;
  Change: string;
  Open: string;
  High: string;
  Low: string;
  TradingShares: string;
  TransactionAmount?: string;
  TransactionNumber?: string;
  LatestBidPrice?: string;
  LatesAskPrice?: string;
  Capitals?: string;
}

export interface RawTpexEsbQuote {
  Date: string;
  SecuritiesCompanyCode: string;
  CompanyName: string;
  PreviousAveragePrice?: string;
  BuyingPrice?: string;
  BuyingQuantity?: string;
  SellingPrice?: string;
  SellingQuantity?: string;
  Highest?: string;
  Lowest?: string;
  Average?: string;
  LatestPrice?: string;
  'Buy/Sell'?: string;
  SuspendTime?: string;
  TransactionVolume?: string;
}

export type TpexMarketSource = 'tpex-mainboard' | 'tpex-esb';

export interface TpexQuote {
  symbol: string;
  name: string;
  close: number;
  change: number;
  changePercent: number;
  open: number;
  high: number;
  low: number;
  volume: number;
  turnover: number;
  transactionCount: number;
  bidPrice?: number;
  askPrice?: number;
  lastUpdated?: Date;
  source: TpexMarketSource;
}

class TpexApiService {
  private readonly BASE_URL = 'https://www.tpex.org.tw/openapi/v1';
  private readonly QUOTES_ENDPOINT = '/tpex_mainboard_quotes';
  private readonly ESB_ENDPOINT = '/tpex_esb_latest_statistics';
  private readonly CACHE_KEY_MAINBOARD = 'tpex_mainboard_quotes';
  private readonly CACHE_KEY_ESB = 'tpex_esb_quotes';
  private readonly CACHE_TTL = 60 * 1000; // 1 minute cache
  private readonly inflightRequests = new Map<string, Promise<any[]>>();

  private normalizeSymbol(symbol: string): string {
    return symbol.trim().toUpperCase();
  }

  async getEsbStockQuote(symbol: string): Promise<TpexQuote | null> {
    const normalized = this.normalizeSymbol(symbol);
    if (!normalized) {
      return null;
    }

    const quotes = await this.getEsbQuotesBySymbols([normalized]);
    return quotes.get(normalized) ?? null;
  }

  private parseNumber(value?: string): number {
    if (!value) {
      return 0;
    }
    const normalized = value
      .replace(/,/g, '')
      .replace('＋', '+')
      .replace('－', '-')
      .replace(/\s/g, '');

    if (!normalized || normalized === '--' || normalized === '---') {
      return 0;
    }

    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private parseInt(value?: string): number {
    if (!value) {
      return 0;
    }
    const normalized = value.replace(/,/g, '');
    const parsed = parseInt(normalized, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private parseTaiwanDate(dateStr?: string): Date | undefined {
    if (!dateStr || dateStr.length < 7) {
      return undefined;
    }
    const year = parseInt(dateStr.substring(0, 3), 10) + 1911;
    const month = parseInt(dateStr.substring(3, 5), 10);
    const day = parseInt(dateStr.substring(5, 7), 10);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return undefined;
    }
    return new Date(year, month - 1, day);
  }

  private transformMainboardQuote(raw: RawTpexQuote): TpexQuote | null {
    const symbol = this.normalizeSymbol(raw.SecuritiesCompanyCode);
    if (!symbol) {
      return null;
    }

    const close = this.parseNumber(raw.Close);
    const change = this.parseNumber(raw.Change);
    const previousClose = close - change;
    const changePercent = previousClose !== 0 ? (change / previousClose) * 100 : 0;

    return {
      symbol,
      name: raw.CompanyName || symbol,
      close,
      change,
      changePercent: Number.isFinite(changePercent) ? changePercent : 0,
      open: this.parseNumber(raw.Open),
      high: this.parseNumber(raw.High),
      low: this.parseNumber(raw.Low),
      volume: this.parseInt(raw.TradingShares),
      turnover: this.parseInt(raw.TransactionAmount),
      transactionCount: this.parseInt(raw.TransactionNumber),
      bidPrice: this.parseNumber(raw.LatestBidPrice),
      askPrice: this.parseNumber(raw.LatesAskPrice),
      lastUpdated: this.parseTaiwanDate(raw.Date),
      source: 'tpex-mainboard'
    };
  }

  private transformEsbQuote(raw: RawTpexEsbQuote): TpexQuote | null {
    const symbol = this.normalizeSymbol(raw.SecuritiesCompanyCode);
    if (!symbol) {
      return null;
    }

    const close = this.parseNumber(raw.LatestPrice);
    const previous = this.parseNumber(raw.PreviousAveragePrice);
    const change = previous !== 0 ? close - previous : 0;
    const changePercent = previous !== 0 ? (change / previous) * 100 : 0;

    return {
      symbol,
      name: raw.CompanyName || symbol,
      close,
      change,
      changePercent: Number.isFinite(changePercent) ? changePercent : 0,
      open: this.parseNumber(raw.Average),
      high: this.parseNumber(raw.Highest),
      low: this.parseNumber(raw.Lowest),
      volume: this.parseInt(raw.TransactionVolume),
      turnover: 0,
      transactionCount: 0,
      bidPrice: this.parseNumber(raw.BuyingPrice),
      askPrice: this.parseNumber(raw.SellingPrice),
      lastUpdated: this.parseTaiwanDate(raw.Date),
      source: 'tpex-esb'
    };
  }

  private async fetchQuotes(cacheKey: string, endpoint: string): Promise<any[]> {
    const cached = stockDataCache.get(cacheKey) as any[] | null;
    if (cached) {
      return cached;
    }

    const inflightRequest = this.inflightRequests.get(cacheKey);
    if (inflightRequest) {
      return inflightRequest;
    }

    const requestPromise = (async () => {
      try {
        logger.api.info('Fetching TPEx quotes via CORS proxy', { endpoint });
        // 使用指數退避重試機制，網路不穩時自動重試
        // 注意：corsProxyService 內部已有 5 層代理瀑布與斷路器，此處僅重試 1 次以避免阻塞過久
        const response = await RetryHelper.apiRetry(
          () => corsProxyService.fetch(`${this.BASE_URL}${endpoint}`),
          {
            maxRetries: 1,
            baseDelay: 2000,
            maxDelay: 10000,
          }
        );
        const data = await response.json();

        if (!Array.isArray(data)) {
          throw new Error('TPEx response is not an array');
        }
        stockDataCache.set(cacheKey, data, this.CACHE_TTL);
        return data;
      } catch (error) {
        const appError = ErrorHandler.handleError(error, 'TpexApiService.fetchQuotes');
        logger.api.error('Failed to fetch TPEx quotes', { endpoint, error: appError });
        throw appError;
      } finally {
        this.inflightRequests.delete(cacheKey);
      }
    })();

    this.inflightRequests.set(cacheKey, requestPromise);
    return requestPromise;

  }
  private async fetchMainboardQuotes(): Promise<RawTpexQuote[]> {
    return this.fetchQuotes(this.CACHE_KEY_MAINBOARD, this.QUOTES_ENDPOINT) as Promise<RawTpexQuote[]>;
  }

  private async fetchEsbQuotes(): Promise<RawTpexEsbQuote[]> {
    return this.fetchQuotes(this.CACHE_KEY_ESB, this.ESB_ENDPOINT) as Promise<RawTpexEsbQuote[]>;
  }

  async getQuotesBySymbols(symbols: string[]): Promise<Map<string, TpexQuote>> {
    if (symbols.length === 0) {
      return new Map();
    }

    const targetSet = new Set(symbols.map(symbol => this.normalizeSymbol(symbol)));
    const quotes = await this.fetchMainboardQuotes();
    const results = new Map<string, TpexQuote>();

    for (const raw of quotes) {
      const normalizedSymbol = this.normalizeSymbol(raw.SecuritiesCompanyCode);
      if (targetSet.has(normalizedSymbol)) {
        const transformed = this.transformMainboardQuote(raw);
        if (transformed) {
          results.set(normalizedSymbol, transformed);
        }
      }
    }

    logger.api.info('Resolved TPEx quotes for symbols', {
      requested: symbols.length,
      resolved: results.size
    });

    return results;
  }

  async getStockQuote(symbol: string): Promise<TpexQuote | null> {
    const normalized = this.normalizeSymbol(symbol);
    if (!normalized) {
      return null;
    }

    const quotes = await this.getQuotesBySymbols([normalized]);
    return quotes.get(normalized) ?? null;
  }
  async getEsbQuotesBySymbols(symbols: string[]): Promise<Map<string, TpexQuote>> {
    if (symbols.length === 0) {
      return new Map();
    }

    const targetSet = new Set(symbols.map(symbol => this.normalizeSymbol(symbol)));
    const quotes = await this.fetchEsbQuotes();
    const results = new Map<string, TpexQuote>();

    for (const raw of quotes) {
      const normalizedSymbol = this.normalizeSymbol(raw.SecuritiesCompanyCode);
      if (!targetSet.has(normalizedSymbol)) {
        continue;
      }

      const transformed = this.transformEsbQuote(raw);
      if (transformed) {
        results.set(normalizedSymbol, transformed);
      }
    }

    logger.api.info('Resolved TPEx ESB quotes for symbols', {
      requested: symbols.length,
      resolved: results.size
    });

    return results;
  }

  clearCache(): void {
    stockDataCache.delete(this.CACHE_KEY_MAINBOARD);
    stockDataCache.delete(this.CACHE_KEY_ESB);
  }
}

export const tpexApiService = new TpexApiService();
export default tpexApiService;
